import {WidgetFullEditorPortal} from "../common/WidgetFullEditorPortal"
import {MarkdownViewer} from "@dv5/components"
import {MarkdownWidgetData} from "@dv5/domain"
import {FC} from "react"
import {useWidgetEditState} from "../../hooks/useWidgetEditState"
import {useWidgetEditor} from "../../hooks/useWidgetEditor"
import {EditorWidgetProps} from "../../model/WidgetProps"
import {widgetService} from "../../services/widgetService"
import {WidgetFrame} from "../common/WidgetFrame"
import {MarkdownWidgetInlineEditor} from "./MarkdownWidgetInlineEditor"
import {MarkdownWidgetToolbar} from "./MarkdownWidgetToolbar"
import {MarkdownWidgetFullEditor} from "./MarkdownWidgetFullEditor"
import './MarkdownWidget.less'

type Props = EditorWidgetProps

export const MarkdownWidget: FC<Props> = ({
                                              canvasId,
                                              widget,
                                              selected,
                                              settings,
                                              editingWidgetId,
                                              onStartDrag,
                                              onStartResize,
                                              onContextMenu,
                                              dragState,
                                              onStartLinkCreation,
                                              onCompleteLinkCreation,
                                              onNavigate,
                                              onDeleteDataLink,
                                              onStopEditing,
                                          }) => {
    const data = widget.data as MarkdownWidgetData
    const {isFullEditor, setIsFullEditor, stopEditing} = useWidgetEditState(onStopEditing, settings.readOnly)

    const {
        containerRef,
        resizeCursor,
        handlers,
        renderResizeHandles,
        renderLinkDots,
    } = useWidgetEditor({
        widget,
        selected,
        editingWidgetId,
        dragState,
        onStartDrag,
        onStartResize,
        onContextMenu,
        onStartLinkCreation,
        onCompleteLinkCreation,
        onNavigate,
        onDeleteDataLink,
    })

    if (editingWidgetId && editingWidgetId === widget.id) {
        const toolbarStyles = widgetService.getToolbarStyle(widget)
        return (
            <div className='markdown-widget-edit-container' ref={containerRef} onContextMenu={handlers.onContextMenu}>
                {!isFullEditor && (
                    <MarkdownWidgetToolbar
                        canvasId={canvasId}
                        widgetId={widget.id}
                        data={data}
                        toolbarStyles={toolbarStyles}
                        onExpandToFull={() => setIsFullEditor(true)}
                    />
                )}
                <div
                    className={widgetService.getWidgetClasses(widget, 'markdownWidget', selected, editingWidgetId, widget.commentsCount > 0)}
                    style={widgetService.getWidgetStyle(widget)}
                >
                    {isFullEditor ? (
                        <MarkdownViewer
                            sourceCode={data.text || ''}
                            compactMode={data.compact ?? false}
                            onToggleCheckBox={() => {}}
                        />
                    ) : (
                        <MarkdownWidgetInlineEditor
                            widgetId={widget.id}
                            text={data.text}
                            canvasId={canvasId}
                            data={data}
                            containerRef={containerRef}
                        />
                    )}
                </div>
                {isFullEditor && (
                    <WidgetFullEditorPortal>
                        <MarkdownWidgetFullEditor
                            canvasId={canvasId}
                            widgetId={widget.id}
                            data={data}
                            toolbarStyles={toolbarStyles}
                            onClose={stopEditing}
                        />
                    </WidgetFullEditorPortal>
                )}
            </div>
        )
    }

    return (
        <WidgetFrame
            baseClass='markdownWidget'
            canvasId={canvasId}
            widget={widget}
            settings={settings}
            selected={selected}
            editingWidgetId={editingWidgetId}
            resizeCursor={resizeCursor}
            handlers={handlers}
            containerRef={containerRef}
            renderResizeHandles={renderResizeHandles}
            renderLinkDots={renderLinkDots}
            onNavigate={onNavigate}
            onDeleteDataLink={onDeleteDataLink}
        >
            <MarkdownViewer
                sourceCode={data.text || ''}
                compactMode={data.compact ?? false}
                onToggleCheckBox={() => {}}
            />
        </WidgetFrame>
    )
}
